// University of Minnesota Twin Cities: public college page (#2743), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UMN Twin Cities' Common Data Set 2025-2026 (cds_2025_2026_tc_0.pdf), the
//    file the Office of Institutional Data and Research links from its Common
//    Data Set page: C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21/C22 early plans. Its
//    G1 block checks the box saying final 2026-2027 costs were not available
//    yet (expected July 1), so costs come from One Stop instead.
//  - UMN Office of Admissions (admissions.tc.umn.edu): Freshman Admission
//    (deadlines, decision dates, July 1 documents), Freshman Application
//    Deadlines (nursing), ACT/SAT Information, and FAQs about how the college
//    of admission is set. The Freshman Admission page says applicants
//    "through fall 2027" need no test score; the ACT/SAT page, the fuller
//    statement, says "through the fall 2029 term". Both cover this cycle.
//  - School of Music, Apply (cla.umn.edu): the 2027 music application
//    deadline and audition dates.
//  - One Stop Student Services, Cost of attendance for undergraduates
//    2026-27 (onestop.umn.edu), resident and nonresident "Living in dorm"
//    columns, read in a browser (the page blocks plain downloads). The
//    budgets include loan fees, kept here so the totals match One Stop's.
//  - College Scorecard, school id 174066 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UMN Twin Cities Common Data Set 2025-2026',
        url: 'https://idr.umn.edu/institutional-metrics-compliance-reporting/common-data-set',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UMN Admissions, Freshman Admission',
        url: 'https://admissions.tc.umn.edu/admissions/freshman-admission-university-minnesota-twin-cities',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UMN Admissions, Freshman Application Deadlines',
        url: 'https://admissions.tc.umn.edu/apply/freshman-application-deadlines',
        read: '2026-10-01',
    },
    tests: {
        label: 'UMN Admissions, ACT / SAT Information',
        url: 'https://admissions.tc.umn.edu/admissions/freshman-admission/act-sat-information',
        read: '2026-10-01',
    },
    collegeAdmit: {
        label: 'UMN Admissions, How We Determine College of Admission',
        url: 'https://admissions.tc.umn.edu/admissions/freshman-admission/frequently-asked-questions-about-how-we-determine-college-admissions',
        read: '2026-10-01',
    },
    music: {
        label: 'UMN School of Music, Apply',
        url: 'https://cla.umn.edu/music/undergraduate/apply',
        read: '2026-10-01',
    },
    coa: {
        label: 'UMN One Stop, Cost of attendance',
        url: 'https://onestop.umn.edu/finances/costs/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?174066-University-of-Minnesota-Twin-Cities',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-minnesota-twin-cities',
    kbId: 'university_of_minnesota_twin_cities',
    name: 'University of Minnesota Twin Cities',
    shortName: 'Minnesota',
    aliases: ['UMN', 'U of M'],
    location: 'Minneapolis, MN',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UMN colors: Maroon, Gold); decoration only.
    branding: {
        primary: '#7A0019',
        secondary: '#FFCC33',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_minnesota_twin_cities_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 43929,
        admitted: 34648,
        enrolled: 7180,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 9780, admitted: 6222 },
            { group: 'Out-of-state', applied: 26246, admitted: 22386 },
            { group: 'International', applied: 7903, admitted: 6040 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UMN\'s Common Data Set reports one first-year acceptance rate for the Twin Cities campus. The admissions office publishes the grades and scores of admitted students by college, but no admission rate by college or major.',
        source: 'collegeAdmit',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UMN has no early decision plan. Its two early action deadlines are not binding and not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You are admitted to a freshman-admitting college. UMN considers you first for the colleges you list, in order, and then for its other colleges that match your interests and preparation. Two programs ask for more:',
        rows: [
            { school: 'School of Nursing', requirement: 'Apply to UMN by November 1. Nursing decisions are released once the whole nursing pool has been reviewed; admitted students should expect a decision by December 15.', source: 'deadlines' },
            { school: 'School of Music', requirement: 'The Bachelor of Music (performance, music education, music therapy) and the BA in music with applied emphasis need a separate School of Music application by December 1, 2026, and an audition on January 30 or February 13, 2027. Music education and music therapy applicants also interview. Admission to UMN and to the School of Music are separate decisions.', source: 'music' },
        ],
        source: 'collegeAdmit',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular deadline', value: 'January 1' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 1', decision: 'By January 31', binding: false },
            { plan: 'Early Action II', deadline: 'December 1', decision: 'By February 28', binding: false },
            { plan: 'Regular deadline', deadline: 'January 1', decision: 'By March 31', binding: false },
        ],
        note: 'The application opens in August and asks for the application, a $55 fee and the courses and grades you enter yourself; no essay or letters are required. Complete applications by these deadlines get full consideration for the University Honors Program and scholarships; later ones are considered as space and funds allow. Admitted students confirm by May 1, and final transcripts are due July 1.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UMN does not require the ACT or SAT from freshman applicants through the fall 2029 term. On the Common App you say whether you want a score considered, and that choice is final once you submit, except when a test is canceled or you cannot get a seat. Applications with and without scores get full consideration for admission, scholarships and the University Honors Program; a score you choose to send is read as one academic factor.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [19312, 43348] },
            { item: 'Books, course materials, supplies, and equipment', amounts: [1000, 1000] },
            { item: 'Housing and food (living in a dorm)', amounts: [15552, 15552] },
            { item: 'Transportation', amounts: [200, 1700] },
            { item: 'Personal and other expenses', amounts: [2000, 2000] },
            { item: 'Loan fees', amounts: [162, 162] },
        ],
        totals: [38226, 63762],
        note: 'One Stop\'s fall and spring budget for an undergraduate living in a dorm, at 13 semester hours each semester. The Carlson School of Management and the College of Science and Engineering each add $3,100 a year in tuition, not shown here. International students have a separate budget.',
        source: 'coa',
        netPrice: {
            average: 17330,
            byIncome: [
                { income: '$0 to $30,000', amount: 5885 },
                { income: '$30,001 to $48,000', amount: 7188 },
                { income: '$48,001 to $75,000', amount: 9031 },
                { income: '$75,001 to $110,000', amount: 16210 },
                { income: 'Over $110,000', amount: 29424 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UMN\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
